import { useLocation, Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { getSectionByPath } from '@/lib/sections';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Blocks a restricted account from rendering a section it may not use.
 *
 * Wrapping the layout outlet once covers every route, including ones added
 * later, and works for direct URL entry as well as in-app navigation. This is
 * only about not showing a broken page — the API enforces the real boundary, so
 * a user who bypasses this sees empty results and 403s rather than data.
 */
export function SectionGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { canAccessSection } = useAuth();

  const section = getSectionByPath(location.pathname);

  // Unknown paths (home, 404) are not section-scoped and fall through.
  if (!section || canAccessSection(section.id)) {
    return <>{children}</>;
  }

  return (
    <div className="flex items-center justify-center min-h-[70vh] px-8">
      <div className="text-center max-w-sm">
        <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-5 h-5 text-slate-400" />
        </div>
        <h2 className="text-base font-semibold text-slate-900 mb-1.5">
          {section.name} isn&apos;t available
        </h2>
        <p className="text-sm text-slate-500 leading-relaxed mb-5">
          Your account doesn&apos;t have access to this section. Ask an admin if you
          think you should.
        </p>
        <Link
          to="/"
          className="inline-flex items-center justify-center h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors"
        >
          Go to overview
        </Link>
      </div>
    </div>
  );
}
